import React, { useState, useContext, useEffect } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { AccountContext } from '@/contexts/account'
import { useHistory } from 'react-router-dom'
import * as Api from '@/apis/aipass'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import * as SurveyModel from '@/models/survey'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SurveyTab } from '@/components/molecules/settings/survey-tab'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { LoadingFull } from '@/components/molecules/loading-full'
import { GuestAppTableHeader, GuestAppTableBody, GuestAppTableRow } from '@/components/atoms/settings/guest-app-table'
import { ContainerHeader } from '@/components/molecules/settings/container-header'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'

export const SettingSurvey: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { account } = useContext<any>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isToggling, setIsToggling] = useState<boolean>(false)
  const history = useHistory()
  const [surveys, setSurveys] = useState<SurveyModel.SurveyType[]>([])
  const [originalSurveys, setOriginalSurveys] = useState<SurveyModel.SurveyType[]>([])
  const [isShowFooter, setIsShowFooter] = useState<boolean>(false)
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)
  const [isMounted, setIsMounted] = useState<boolean>(true)
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const toggleViewStatus = async (changedItem: SurveyModel.SurveyType) => {
    if (!guardSettingsEdit()) return
    // 通信中は処理をスキップ
    if (isToggling) return

    try {
      setIsToggling(true)
      const newViewStatus = changedItem.viewStatus ? 0 : 1
      const isVisible = Boolean(newViewStatus)

      setSurveys(prevSurveys =>
        prevSurveys.map(survey => (survey.surveyId === changedItem.surveyId ? { ...survey, viewStatus: newViewStatus } : survey)),
      )

      // API呼び出し
      await Api.updateSurveyVisibility(changedItem.surveyId, isVisible)

      // 成功時はoriginalSurveysも更新
      setOriginalSurveys(prevOriginal =>
        prevOriginal.map(survey => (survey.surveyId === changedItem.surveyId ? { ...survey, viewStatus: newViewStatus } : survey)),
      )
    } catch (error) {
      console.error('Failed to toggle view status:', error)
      // エラー時は元の状態に戻す
      setSurveys(originalSurveys)
    } finally {
      setIsToggling(false)
    }
  }

  const addDeleteTarget = (id: string) => {
    if (!guardSettingsEdit()) return
    // 通信中は処理をスキップ
    if (isToggling) return

    setSurveys(surveys.filter(survey => survey.surveyId !== id))
    setDeleteIds([...deleteIds, id])
    setIsShowFooter(true)
  }

  const onClickFooterCancelButton = () => {
    // 元の状態に戻す
    setSurveys(originalSurveys)
    setDeleteIds([])
    setIsShowFooter(false)
  }

  const onClickFooterSaveButton = async () => {
    try {
      setIsLoading(true)

      // 削除処理
      if (deleteIds.length > 0) {
        await Api.deleteSurveys(deleteIds)
      }

      // 削除対象を除外したリストを作成
      const remainingSurveys = surveys.filter(survey => !deleteIds.includes(survey.surveyId))

      // 並び替えの変更を保存（削除対象を除外）
      if (isShowFooter) {
        const orderData = remainingSurveys.map((survey, index) => ({
          questionId: survey.surveyId,
          sortOrder: index + 1,
        }))
        await Api.updateSurveyOrder(orderData)
      }

      // データを再取得して最新状態に更新
      await fetchSurvey()
      setDeleteIds([])
      setIsShowFooter(false)
    } catch (error) {
      console.error('Failed to save changes:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSurvey = async () => {
    try {
      const res = await Api.fetchSurvey()
      // コンポーネントがアンマウントされている場合は処理を停止
      if (!isMounted) return

      // 型ガードでSurveyListResponseTypeかどうかを確認
      if (res && 'surveys' in res) {
        const surveysData = res.surveys || []
        setSurveys(surveysData)
        setOriginalSurveys(surveysData)
      } else {
        setSurveys([])
        setOriginalSurveys([])
      }
      setIsInitialLoading(false)
    } catch (error) {
      console.error('Failed to fetch surveys:', error)
      if (isMounted) {
        setSurveys([])
        setIsInitialLoading(false)
      }
    }
  }

  const changeDisplayOrder = (newList: SurveyModel.SurveyType[]) => {
    if (!guardSettingsEdit()) return
    // 通信中は処理をスキップ
    if (isToggling) return

    setSurveys(newList)
    setIsShowFooter(true)
  }

  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom<SurveyModel.SurveyType>({
    list: surveys,
    onChange: changeDisplayOrder,
    key: 'surveyId',
  })

  useEffect(() => {
    if (account && account?.hotel) {
      fetchSurvey()
    }

    // クリーンアップ関数
    return () => {
      setIsMounted(false)
    }
  }, [account])

  const footerContent =
    isShowFooter || deleteIds.length ? <EditFooter onSave={onClickFooterSaveButton} onCancel={onClickFooterCancelButton} /> : undefined

  if (isInitialLoading) {
    return <LoadingFull isLoading={isInitialLoading} />
  }

  return (
    <>
      <SettingsLayout loading={isLoading} footerContent={footerContent}>
        <TabContainer tabComponent={<SurveyTab currentTab="Item" />} isShowFooter={isShowFooter || deleteIds.length > 0}>
          <>
            <ContainerHeader
              onClickAddButton={
                surveys.length >= 15 || isToggling || isSettingsReadOnly
                  ? undefined
                  : () => {
                      if (!guardSettingsEdit()) return
                      history.push('/setting/survey/new')
                    }
              }
            />
            {surveys.length >= 15 && (
              <div css={maxLimitMessageContainerStyle}>
                <span css={maxLimitMessageStyle}>{t('Maximum 15 questions can be registered')}</span>
              </div>
            )}
            <GuestAppTableHeader>
              <div css={itemNameColStyle}>{t('Item name')}</div>
              <div css={questionTextColStyle}>{t('Question text')}</div>
              <div css={answerFormatColStyle}>{t('Answer format')}</div>
              <div css={toggleColStyle}></div>
              <div css={deleteColStyle}></div>
            </GuestAppTableHeader>
            <GuestAppTableBody>
              {surveys.map(survey => (
                <GuestAppTableRow
                  className={survey.isFixed ? '' : 'is-custom'}
                  id={survey.surveyId}
                  key={survey.surveyId}
                  onClick={() => {
                    if (!survey.isFixed && !isToggling && guardSettingsEdit()) {
                      history.push(`/setting/survey/${survey.surveyId}`)
                    }
                  }}
                  draggable={!isToggling && !isSettingsReadOnly}
                  onDragStart={dragStart}
                  onDragOver={dragOver}
                  onDrop={dragDrop}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div css={itemNameColStyle}>{survey.title?.[i18n.language] || ''}</div>
                  <div css={questionTextColStyle}>{survey.question_text?.[i18n.language] || ''}</div>
                  <div css={answerFormatColStyle}>
                    {survey.type === 'nps'
                      ? 'NPS'
                      : survey.type === 'score'
                      ? t('Score format')
                      : survey.type === 'choice_single' || survey.type === 'choice_multiple'
                      ? t('Selection format')
                      : t('Free description')}
                  </div>
                  <div css={toggleColStyle}>
                    <ToggleButton
                      value={survey?.viewStatus ? true : false}
                      onChange={() => void toggleViewStatus(survey)}
                      disabled={isToggling || isSettingsReadOnly}
                      customCss={css`
                        transform: scale(0.9);
                        background: '#F2A40B';
                        border-radius: '15px';
                      `}
                    />
                  </div>
                  <div css={deleteColStyle}>
                    {!survey.isFixed && (
                      <DeleteIcon onClick={() => addDeleteTarget(survey.surveyId)} disabled={isToggling || isSettingsReadOnly} />
                    )}
                  </div>
                </GuestAppTableRow>
              ))}
            </GuestAppTableBody>
          </>
        </TabContainer>
      </SettingsLayout>
    </>
  )
}

const itemNameColStyle = css({
  paddingLeft: 62,
  flexGrow: 1,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const questionTextColStyle = css({
  flexGrow: 2,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const answerFormatColStyle = css({
  width: 130,
})
const toggleColStyle = css({
  width: 73,
  margin: '0 16px',
})
const deleteColStyle = css({
  width: 30,
  margin: '0 16px',
})

const maxLimitMessageContainerStyle = css({
  padding: '8px 32px 8px 16px',
  fontSize: '10px',
  color: '#676767',
  fontWeight: 'normal',
  borderBottom: '1px solid #F2F2F2',
})

const maxLimitMessageStyle = css({})
